import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/constants";
import { Reveal } from "@/components/Reveal";

const chapters = [
  {
    n: "01",
    title: "A ladies-only sanctuary",
    body: "A private, comfortable space in the heart of Bur Dubai where every woman is cared for — no rush, no compromise, just you and our artists.",
  },
  {
    n: "02",
    title: "Craft in every detail",
    body: "From precision cuts and keratin rituals to delicate henna and flawless nails, our specialists treat beauty as a craft — not a checklist.",
  },
  {
    n: "03",
    title: "Honest, warm & welcoming",
    body: "Rated 4.2★ on Google by the women of Bur Dubai. Fair prices, gentle hands and a cup of tea while we work our magic.",
  },
];

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      ref={ref}
      id="about"
      data-testid="about-section"
      className="py-28 sm:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <Reveal>
            <div className="relative overflow-hidden rounded-sm">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src={IMAGES.portrait}
                alt="Editorial beauty portrait"
                className="w-full aspect-[3/4] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
              <div className="absolute bottom-5 left-5 text-[10px] tracking-[0.3em] uppercase text-bone/80 border border-bone/20 px-3 py-1.5 rounded-full backdrop-blur-sm">
                Est. in Bur Dubai
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-gold">
              Our Philosophy
            </span>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-bone">
              Beauty is a ritual,
              <br />
              <em className="text-gold-gradient italic">not a routine.</em>
            </h2>
          </Reveal>

          <div className="mt-16 space-y-14">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="flex gap-8 items-start group">
                  <span className="font-serif text-5xl sm:text-6xl font-light text-line group-hover:text-gold transition-colors duration-700 leading-none">
                    {c.n}
                  </span>
                  <div className="pt-2 border-t border-line flex-1">
                    <h3 className="font-serif text-2xl sm:text-3xl text-bone pt-5">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-sand leading-relaxed max-w-lg">
                      {c.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
